import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Table } from '../components/ui/Table';
import { Select } from '../components/ui/Input';
import { StatsCard } from '../components/ui/StatsCard';
import { readRequiredJson } from '../utils/adminApiResponse';

const fmtUsd = (usd: any): string => {
  const n = Number(usd || 0);
  if (!Number.isFinite(n)) return '$0.000000';
  if (n === 0) return '$0.000000';
  const abs = Math.abs(n);
  if (abs < 0.0001) return `$${n.toFixed(8)}`;
  if (abs < 0.01)   return `$${n.toFixed(6)}`;
  if (abs < 1)      return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
};
const ICON = (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const fmtQty = (q: any): string => {
  const n = Number(q || 0);
  if (!Number.isFinite(n) || n === 0) return '0';
  if (Math.abs(n) < 1) return n.toFixed(4);
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

type Rate = {
  event_type: string;
  provider: string;
  category: string;
  unit_label: string;
  unit_price_usd: string;
  direct_unit_price_usd: string;
  is_passthrough: boolean;
  billed_via: string | null;
  billed_via_providers: string | null;
  layman_description: string | null;
  source_url: string | null;
  notes: string | null;
  qty_today: string; qty_7d: string; qty_30d: string; qty_3mo: string; qty_6mo: string; qty_12mo: string; qty_all_time: string;
  cost_today: string; cost_7d: string; cost_30d: string; cost_3mo: string; cost_6mo: string; cost_12mo: string; cost_all_time: string;
};
type Invoice = {
  id: string;
  provider: string;
  external_id: string;
  period_start: string | null;
  period_end: string | null;
  paid: boolean | null;
  total_usd: string;
  currency: string;
  portal_url: string | null;
  download_url: string | null;
  fetched_at: string;
};
type Provider = {
  provider: string;
  categories: string[];
  rates_count: number;
  cost_today: number; cost_7d: number; cost_30d: number; cost_3mo: number; cost_6mo: number; cost_12mo: number; cost_all_time: number;
  qty_today: number; qty_all_time: number;
};

// Layman explanations per provider — shown above the rate table for context.
const PROVIDER_BLURBS: Record<string, string> = {
  'Telnyx':              'Voice + messaging carrier — handles every phone call and SMS the platform places or receives. Each call minute, each SMS segment, each phone number and emergency 911 record is billed by Telnyx.',
  'LiveKit':             'Real-time video + audio infrastructure powering the visitor-intercom calls. Pricing is per participant-minute, with extras for recording, streaming and SIP bridging.',
  'AWS Lambda':          'The serverless functions that run every backend feature. Every API call from a phone, kiosk, or webpage triggers a Lambda invocation.',
  'AWS Aurora PostgreSQL': 'The relational source of truth for devices, intercom configurations, sessions, and application data.',
  'AWS API Gateway':     'The front door at api.intrlock.io that routes every HTTPS request to the right Lambda.',
  'AWS S3':              'Object storage — building photos, recordings, brand assets, mobile bundles. Charged for storage volume and request count.',
  'AWS CloudFront':      'CDN that serves the web apps (app/admin/visitor/tenants) close to the user. Each browser request and byte counts.',
  'AWS RDS':             'Managed Postgres database — source of truth for properties, people, billing.',
  'AWS RDS Proxy':       'Connection pooler in front of RDS so Lambdas don\'t exhaust database connections.',
  'AWS CloudWatch':      'Logs and metrics. Lambda logs, custom metrics, alarms, dashboards.',
  'AWS Rekognition':     'Face detection, face recognition and image labelling — used by face-unlock and package detection.',
  'AWS SES':             'Outbound transactional email — receipts, password resets, alerts.',
  'AWS SNS':             'Push notifications, SMS fallback and event fan-out.',
  'AWS KMS':             'Key Management Service — encrypts secrets and signs JWTs.',
  'AWS Secrets Manager': 'Stores database passwords, API keys, OAuth tokens.',
  'AWS Route 53':        'DNS for intrlock.io and all subdomains.',
  'AWS VPC':             'Private network for Lambdas + RDS. NAT Gateway is the largest per-GB cost here.',
  'AWS ELB':             'Application Load Balancer (used internally).',
  'AWS WAF':             'Web Application Firewall — blocks malicious traffic before it hits the apps.',
  'AWS EC2':             'Virtual machines — used for any long-running workload (bridge gateway, integrations).',
  'AWS ECS':             'Container compute (Fargate) for background workers.',
  'AWS EventBridge':     'Internal event bus — fans events out to stripe-reporter, audit log, etc.',
  'AWS Firehose':        'Streams data into S3 for offline analytics.',
  'AWS Athena':          'Ad-hoc SQL over data in S3.',
  'AWS Glue':            'ETL jobs that prepare reporting data.',
  'AWS (composite)':     'Composite cost — page views, image uploads, mobile API calls all touch multiple AWS services; this rate rolls them together.',
  'PDK':                 'Access-control platform — Cloud Nodes, holder seats, mobile credentials. Used for door hardware integration.',
  'Brivo':               'Alternate access-control platform — credentials and mobile passes.',
  'Stripe':              'Payments — card charges, ACH, subscriptions, payouts, refunds.',
  'Twilio':              'Backup voice + SMS carrier (used when Telnyx isn\'t available).',
  'Soracom':             'Cellular IoT carrier — SIM cards for bridges and panel kiosks.',
  'Printify':            'Apparel + merchandise fulfillment for the shop pages.',
};

export const Operations: React.FC = () => {
  const [data, setData] = useState<{ rates: Rate[]; providers: Provider[]; generated_at: string } | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshingInvoices, setRefreshingInvoices] = useState(false);
  const [selected, setSelected] = useState<string>('');
  const [error, setError] = useState('');
  const [invoiceError, setInvoiceError] = useState('');

  const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
  const token = localStorage.getItem('intrlock_token') || '';
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetch(`${API}/stripe/admin/operations/summary`, { headers })
      .then(r => readRequiredJson(
        r,
        (value): value is { rates: Rate[]; providers: Provider[]; generated_at: string } =>
          Array.isArray((value as any)?.rates)
          && Array.isArray((value as any)?.providers)
          && typeof (value as any)?.generated_at === 'string',
        'Operations summary',
      ))
      .then(d => { setData(d); if (d?.providers?.[0]) setSelected(d.providers[0].provider); })
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : String(reason)))
      .finally(() => setLoading(false));
    fetch(`${API}/stripe/admin/operations/invoices`, { headers })
      .then(r => readRequiredJson(
        r,
        (value): value is { invoices: Invoice[] } => Array.isArray((value as any)?.invoices),
        'Operations invoices',
      ))
      .then(d => setInvoices(d.invoices))
      .catch(() => setInvoices([]));
  }, []);

  const refreshInvoices = async () => {
    setRefreshingInvoices(true);
    setInvoiceError('');
    try {
      const refreshed = await fetch(`${API}/stripe/admin/operations/invoices/refresh`, { method: 'POST', headers });
      if (!refreshed.ok) throw new Error(`Invoice refresh returned HTTP ${refreshed.status}`);
      const d = await readRequiredJson(
        await fetch(`${API}/stripe/admin/operations/invoices`, { headers }),
        (value): value is { invoices: Invoice[] } => Array.isArray((value as any)?.invoices),
        'Operations invoices',
      );
      setInvoices(d.invoices);
    } catch (reason: unknown) {
      setInvoiceError(reason instanceof Error ? reason.message : String(reason));
    } finally { setRefreshingInvoices(false); }
  };

  const selectedProvider = useMemo(() => data?.providers.find(p => p.provider === selected), [data, selected]);
  const selectedRates = useMemo(() => (data?.rates || []).filter(r => r.provider === selected), [data, selected]);
  const latestUsageAt = useMemo(() => {
    const timestamps = (data?.rates || [])
      .map((rate: any) => rate.last_seen_at ? new Date(rate.last_seen_at).getTime() : 0)
      .filter(Number.isFinite);
    return timestamps.length ? Math.max(...timestamps) : 0;
  }, [data]);
  const usageIsStale = !latestUsageAt || Date.now() - latestUsageAt > 48 * 60 * 60 * 1000;
  // Group rates by category within the selected provider.
  const groupedByCategory = useMemo(() => {
    const m: Record<string, Rate[]> = {};
    for (const r of selectedRates) {
      m[r.category] = m[r.category] || [];
      m[r.category].push(r);
    }
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b));
  }, [selectedRates]);

  if (loading) return <div className="text-sm text-gray-400 p-8">Loading operations cost catalog…</div>;
  if (!data) return <div className="text-sm text-red-500 p-8">Failed to load /operations/summary{error ? `: ${error}` : ''}</div>;

  const grand = data.providers.reduce((acc, p) => {
    acc.today    += p.cost_today;
    acc.last30d  += p.cost_30d;
    acc.last12mo += p.cost_12mo;
    acc.alltime  += p.cost_all_time;
    return acc;
  }, { today: 0, last30d: 0, last12mo: 0, alltime: 0 });

  return (
    <div className="shadow-page-frame legacy-dashboard-migrated space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Operations</h1>
        <p className="text-gray-500 mt-1">Every billable event by every service provider. Live rates from each provider's pricing page, live usage from <span className="font-mono">usage_ledger</span>.</p>
        <p className="text-xs text-gray-400 mt-1">Last refresh: {new Date(data.generated_at).toLocaleString()} · {data.rates.length} rates across {data.providers.length} providers</p>
      </div>

      {usageIsStale && (
        <div role="alert" className="border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong>Usage import is stale.</strong>{' '}
          Current-period totals may be incomplete and must not be interpreted as zero spend.
          {latestUsageAt ? ` Last metered event: ${new Date(latestUsageAt).toLocaleString()}.` : ' No metered events have been imported.'}
        </div>
      )}

      {/* Grand totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard title="Total Cost Today" value={fmtUsd(grand.today)} icon={ICON} />
        <StatsCard title="Last 30 Days" value={fmtUsd(grand.last30d)} icon={ICON} />
        <StatsCard title="Last 12 Months" value={fmtUsd(grand.last12mo)} icon={ICON} />
        <StatsCard title="All Time" value={fmtUsd(grand.alltime)} icon={ICON} />
      </div>

      {/* Provider rollup */}
      <Card padding={false}>
        <CardHeader title="Provider Rollup" subtitle="Click a row or use the dropdown below to drill into a provider's per-event rates and usage" />
        <Table
          data={data.providers.map(p => ({ ...p, id: p.provider }))}
          columns={[
            { key: 'provider', header: 'Provider', render: (r: any) => (
              <button onClick={() => setSelected(r.provider)} className="text-left">
                <div className="font-semibold text-gray-900 hover:text-emerald-700">{r.provider}</div>
                <div className="text-xs text-gray-500">{r.categories.join(' · ')}</div>
              </button>
            )},
            { key: 'rates_count', header: 'Rates',    render: (r: any) => <span className="text-xs text-gray-600">{r.rates_count}</span> },
            { key: 'qty_today',   header: 'Qty Today',render: (r: any) => fmtQty(r.qty_today) },
            { key: 'cost_today',  header: 'Today',    render: (r: any) => <span className="font-mono text-xs">{fmtUsd(r.cost_today)}</span> },
            { key: 'cost_30d',    header: '30d',      render: (r: any) => <span className="font-mono text-xs">{fmtUsd(r.cost_30d)}</span> },
            { key: 'cost_12mo',   header: '12mo',     render: (r: any) => <span className="font-mono text-xs">{fmtUsd(r.cost_12mo)}</span> },
            { key: 'cost_all',    header: 'All Time', render: (r: any) => <span className="font-mono text-xs font-semibold">{fmtUsd(r.cost_all_time)}</span> },
          ]}
        />
      </Card>

      {/* Drill-down: selected provider */}
      <Card>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-4">
          <div className="flex-1">
            <Select
              label="Service Provider"
              value={selected}
              onChange={e => setSelected(e.target.value)}
            >
              {data.providers.map(p => (
                <option key={p.provider} value={p.provider}>
                  {p.provider} ({p.rates_count} rates · {fmtUsd(p.cost_30d)} last 30d)
                </option>
              ))}
            </Select>
          </div>
          {selectedProvider && (
            <div className="flex gap-2">
              <Badge color="green">{selectedProvider.rates_count} rates</Badge>
              {selectedProvider.categories.map(c => <Badge key={c} color="gray">{c}</Badge>)}
            </div>
          )}
        </div>
        {selectedProvider && (
          <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-900">
            <strong className="text-emerald-700">{selectedProvider.provider}.</strong>{' '}
            {PROVIDER_BLURBS[selectedProvider.provider] || 'Service provider in the Intrlock cost stack.'}
          </div>
        )}
        {(() => {
          const allPass = selectedRates.length > 0 && selectedRates.every(r => r.is_passthrough);
          if (!allPass) return null;
          return (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
              <strong>{selectedProvider?.provider}</strong> does not bill Intrlock directly.
              Every event below incurs cost only through its underlying service (typically AWS Lambda, API Gateway, and Aurora PostgreSQL).
              The rates shown are the <em>effective</em> per-event cost. The provider total stays $0 to avoid double-counting against the AWS rollup that already captures the same dollars.
            </div>
          );
        })()}
        {selectedProvider && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatsCard title="Today" value={fmtUsd(selectedProvider.cost_today)} icon={ICON} />
            <StatsCard title="7 Days" value={fmtUsd(selectedProvider.cost_7d)} icon={ICON} />
            <StatsCard title="30 Days" value={fmtUsd(selectedProvider.cost_30d)} icon={ICON} />
            <StatsCard title="3 Months" value={fmtUsd(selectedProvider.cost_3mo)} icon={ICON} />
            <StatsCard title="6 Months" value={fmtUsd(selectedProvider.cost_6mo)} icon={ICON} />
            <StatsCard title="12 Months" value={fmtUsd(selectedProvider.cost_12mo)} icon={ICON} />
            <StatsCard title="All Time" value={fmtUsd(selectedProvider.cost_all_time)} icon={ICON} />
            <StatsCard title="Qty Today" value={fmtQty(selectedProvider.qty_today)} icon={ICON} />
          </div>
        )}
      </Card>

      {/* Invoices for selected provider */}
      {selected && (() => {
        const provInvoices = invoices.filter(i => i.provider === selected);
        return (
          <Card padding={false}>
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{selected} Invoices</h3>
                <p className="text-xs text-gray-500">Real billing periods from {selected} — refresh pulls the latest from the provider's API.</p>
              </div>
              <button
                onClick={refreshInvoices}
                disabled={refreshingInvoices}
                className="text-xs px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {refreshingInvoices ? 'Refreshing…' : 'Refresh invoices'}
              </button>
            </div>
            {invoiceError && (
              <div role="alert" className="m-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
                Invoice refresh failed: {invoiceError}
              </div>
            )}
            {provInvoices.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">
                No invoices on file for {selected}.{' '}
                {selected.startsWith('AWS') ? (
                  <span>AWS doesn't expose an invoice API — monthly periods are synthesized from Cost Explorer (click Refresh).</span>
                ) : ['Telnyx','Stripe'].includes(selected) ? (
                  <span>Click Refresh invoices to pull from {selected}.</span>
                ) : (
                  <span>{selected} doesn't expose an invoice API.</span>
                )}
              </div>
            ) : (
              <Table
                data={provInvoices.map(i => ({ ...i }))}
                columns={[
                  { key: 'period_start', header: 'Period', render: (r: any) => r.period_start ? (
                    <span className="text-xs">{new Date(r.period_start).toLocaleDateString()} → {r.period_end ? new Date(r.period_end).toLocaleDateString() : '—'}</span>
                  ) : '—' },
                  { key: 'external_id',  header: 'Invoice ID', render: (r: any) => <span className="font-mono text-xs">{r.external_id}</span> },
                  { key: 'total_usd',    header: 'Total',      render: (r: any) => <span className="font-mono text-sm font-semibold">{fmtUsd(r.total_usd)}</span> },
                  { key: 'paid',         header: 'Status',     render: (r: any) => r.paid ? <Badge color="green">Paid</Badge> : <Badge color="amber">Unpaid</Badge> },
                  { key: 'links',        header: 'Links',      render: (r: any) => (
                    <div className="flex gap-3 text-xs">
                      {r.portal_url   && <a href={r.portal_url}   target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">View</a>}
                      {r.download_url && <a href={r.download_url} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">Download PDF</a>}
                    </div>
                  )},
                  { key: 'fetched_at',   header: 'Last Synced',render: (r: any) => <span className="text-xs text-gray-500">{new Date(r.fetched_at).toLocaleString()}</span> },
                ]}
              />
            )}
          </Card>
        );
      })()}

      {/* Per-category rate tables */}
      {groupedByCategory.map(([cat, rates]) => (
        <Card key={cat} padding={false}>
          <CardHeader
            title={`${cat} — ${selected}`}
            subtitle={`${rates.length} billable event types · click a rate to view provider documentation`}
          />
          <Table
            data={rates.map(r => ({ ...r, id: r.event_type }))}
            columns={[
              { key: 'event_type', header: 'Event', render: (r: any) => (
                <div>
                  <span className="font-mono text-xs text-gray-900">{r.event_type}</span>
                  {r.is_passthrough && (
                    <span className="ml-2 inline-block text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                      billed via {r.billed_via_providers || 'AWS'}
                    </span>
                  )}
                  {r.layman_description && <span className="block text-[11px] text-gray-500 italic mt-0.5">{r.layman_description}</span>}
                  {r.is_passthrough && r.billed_via && (
                    <span className="block text-[10px] font-mono text-gray-400 mt-0.5">↳ {r.billed_via}</span>
                  )}
                </div>
              )},
              { key: 'unit_price_usd', header: 'Rate', render: (r: any) => (
                <span className="font-mono text-xs">
                  {r.is_passthrough ? (
                    <>
                      <span className="text-gray-400 line-through">{fmtUsd(r.direct_unit_price_usd)}</span>{' '}
                      <span className="font-semibold text-amber-700">{fmtUsd(r.unit_price_usd)}</span>
                    </>
                  ) : fmtUsd(r.unit_price_usd)}
                  {r.source_url && <a href={r.source_url} target="_blank" rel="noopener noreferrer" aria-label="Open source record" className="mobile-touch-link ml-1 text-emerald-600 hover:underline">↗</a>}
                </span>
              )},
              { key: 'unit_label',  header: 'Billing Unit', render: (r: any) => (
                <span className="font-mono text-[11px] text-gray-600">{r.unit_label || '—'}</span>
              )},
              { key: 'cost_today',  header: 'Spend 24h', render: (r: any) => (
                <span className={`font-mono text-xs ${Number(r.cost_today) > 0 ? 'font-semibold text-gray-900' : 'text-gray-400'}`}>
                  {fmtUsd(r.cost_today)}
                </span>
              )},
              { key: 'qty_today',   header: 'Today',   render: (r: any) => <span className="font-mono text-xs">{fmtQty(r.qty_today)}</span> },
              { key: 'qty_7d',      header: '7d',      render: (r: any) => <span className="font-mono text-xs">{fmtQty(r.qty_7d)}</span> },
              { key: 'qty_30d',     header: '30d',     render: (r: any) => <span className="font-mono text-xs">{fmtQty(r.qty_30d)}</span> },
              { key: 'qty_3mo',     header: '3mo',     render: (r: any) => <span className="font-mono text-xs">{fmtQty(r.qty_3mo)}</span> },
              { key: 'qty_6mo',     header: '6mo',     render: (r: any) => <span className="font-mono text-xs">{fmtQty(r.qty_6mo)}</span> },
              { key: 'qty_12mo',    header: '12mo',    render: (r: any) => <span className="font-mono text-xs">{fmtQty(r.qty_12mo)}</span> },
              { key: 'qty_all',     header: 'All Time',render: (r: any) => <span className="font-mono text-xs">{fmtQty(r.qty_all_time)}</span> },
              { key: 'cost_30d',    header: 'Cost 30d',render: (r: any) => <span className="font-mono text-xs">{fmtUsd(r.cost_30d)}</span> },
              { key: 'cost_all',    header: 'Cost All',render: (r: any) => <span className="font-mono text-xs font-semibold">{fmtUsd(r.cost_all_time)}</span> },
            ]}
          />
        </Card>
      ))}
    </div>
  );
};
